import { sql } from 'drizzle-orm';
import {
  pgTable,
  index,
  uniqueIndex,
  jsonb,
  timestamp,
  varchar,
  text,
  integer,
  decimal,
  boolean,
  uuid,
  primaryKey
} from "drizzle-orm/pg-core";
import { claims, timelineEvents } from './claims';
import { contacts } from './contacts';
import { callDirectionEnum, callSourceEnum, callStatusEnum, phoneDepartmentEnum, transcriptionStatusEnum } from './enums';
import { organizations } from './organizations';
import { users } from './users';

// ============================================
// TWILIO CALL SYSTEM TABLES
// ============================================

// Company Phone Lines - The 3 Twilio numbers registered to the company
export const companyPhoneLines = pgTable("company_phone_lines", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Twilio phone number details
  phoneNumber: varchar("phone_number").notNull(), // E.164 format: +1XXXXXXXXXX
  twilioSid: varchar("twilio_sid"), // Twilio phone number SID
  friendlyName: varchar("friendly_name").notNull(), // e.g., "Main Office", "Sales Line", "Support Line"

  // Call forwarding configuration
  forwardingEnabled: boolean("forwarding_enabled").default(true),
  forwardingNumbers: jsonb("forwarding_numbers"), // Array of user phone numbers to forward to

  // Configuration
  voicemailEnabled: boolean("voicemail_enabled").default(true),
  recordingEnabled: boolean("recording_enabled").default(true),
  transcriptionEnabled: boolean("transcription_enabled").default(true),

  // Status
  isActive: boolean("is_active").default(true),

  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_company_phone_lines_number").on(table.phoneNumber),
  uniqueIndex('company_phone_lines_org_phone_unique').on(table.orgId, table.phoneNumber),
]);


// Call Records - Track all calls made through the system
export const callRecords = pgTable("call_records", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  
  // Twilio identifiers
  twilioCallSid: varchar("twilio_call_sid").unique(), // Twilio Call SID
  twilioAccountSid: varchar("twilio_account_sid"),
  
  // Call metadata
  callSource: callSourceEnum("call_source").notNull(),
  direction: callDirectionEnum("direction").notNull(),
  status: callStatusEnum("status").notNull().default("initiated"),
  
  // Phone numbers
  fromNumber: varchar("from_number").notNull(), // Caller's number
  toNumber: varchar("to_number").notNull(), // Recipient's number
  companyLineId: uuid("company_line_id").references(() => companyPhoneLines.id),
  
  // Call details
  startTime: timestamp("start_time"),
  endTime: timestamp("end_time"),
  durationSeconds: integer("duration_seconds"),
  
  // Recording details
  recordingUrl: text("recording_url"),
  recordingSid: varchar("recording_sid"),
  recordingDurationSeconds: integer("recording_duration_seconds"),
  
  // Transcription
  transcriptionStatus: transcriptionStatusEnum("transcription_status").default("pending"),
  transcriptionText: text("transcription_text"),
  transcriptionConfidence: decimal("transcription_confidence", { precision: 5, scale: 4 }),
  transcriptionCompletedAt: timestamp("transcription_completed_at"),
  
  // Claim association
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "set null" }),
  contactId: uuid("contact_id").references(() => contacts.id),
  
  // User who initiated/handled the call
  userId: varchar("user_id").references(() => users.id),
  
  // For email-uploaded recordings
  sourceEmailId: varchar("source_email_id"), // Reference to the Gmail message ID if uploaded via email
  
  // AI Agent details (for future Retell.ai integration)
  aiAgentId: varchar("ai_agent_id"),
  aiAgentName: varchar("ai_agent_name"),
  aiAgentSessionId: varchar("ai_agent_session_id"),
  
  // Notes and metadata
  notes: text("notes"),
  metadata: jsonb("metadata"),

  // Timeline reference
  timelineEventId: uuid("timeline_event_id").references(() => timelineEvents.id),

  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_call_records_claim").on(table.claimId),
  index("idx_call_records_user").on(table.userId),
  index("idx_call_records_twilio_sid").on(table.twilioCallSid),
  index("idx_call_records_status").on(table.status),
  index("idx_call_records_source").on(table.callSource),
  index("idx_call_records_start_time").on(table.startTime),
]);


export const adjusterPhoneDirectory = pgTable("adjuster_phone_directory", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id), // Optional link to CRM user
  name: varchar("name").notNull(),
  phoneNumber: varchar("phone_number").notNull(), // E.164 format
  department: phoneDepartmentEnum("department").notNull(),
  priority: integer("priority").default(1), // Ring order within department (lower = first)
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_adjuster_phone_dir_dept").on(table.department),
  index("idx_adjuster_phone_dir_active").on(table.isActive),
]);
